// R-LINK (Section 5): song link classification, the web mirror of
// backend/routes/services_links.py classify_link(). Keep the two in step.

export const DEFAULT_LABELS = {
  youtube: 'YouTube',
  spotify: 'Spotify',
  apple: 'Apple Music',
  pdf: 'Chord chart',
  drive: 'Google Drive',
  dropbox: 'Dropbox',
  audio: 'Audio',
  other: 'Link',
};

// Kinds that may carry a per-link key (R-LINK 5).
export const KEY_KINDS = ['pdf', 'drive', 'dropbox', 'audio', 'other'];

const HOST_KINDS = [
  [['youtube.com', 'youtu.be', 'youtube-nocookie.com'], 'youtube'],
  [['open.spotify.com', 'spotify.link'], 'spotify'],
  [['music.apple.com'], 'apple'],
  [['drive.google.com', 'docs.google.com'], 'drive'],
  [['dropbox.com', 'dl.dropboxusercontent.com'], 'dropbox'],
];
const AUDIO_RE = /\.(mp3|m4a|aac|wav|ogg|oga|opus|flac|weba)$/i;
const SCHEME_RE = /^[a-zA-Z][a-zA-Z0-9+.-]*:(?![0-9])/;
// eslint-disable-next-line no-control-regex
const BAD_CHARS_RE = /[\s\x00-\x1f\x7f\\<>"{}|^`]/;
export const MAX_URL_LENGTH = 2048;
const YT_ID_RE = /^[A-Za-z0-9_-]{6,}$/;

export class LinkRejected extends Error {}

const hostMatches = (host, h) => host === h || host.endsWith(`.${h}`);

function youtubeVideoId(u) {
  const v = u.searchParams.get('v');
  if (v) return YT_ID_RE.test(v) ? v : null;
  const path = u.pathname || '';
  const host = u.hostname || '';
  const m = path.match(/^\/(?:shorts|embed|live)\/([^/?#]+)/);
  if (m) return YT_ID_RE.test(m[1]) ? m[1] : null;
  if (host === 'youtu.be' || host.endsWith('.youtu.be')) {
    const seg = path.replace(/^\/+|\/+$/g, '').split('/')[0] || '';
    return seg && YT_ID_RE.test(seg) ? seg : null;
  }
  return null; // playlist-only or channel links open out (S-13)
}

// Returns {url, kind, video_id, label, own_storage_warning}; throws LinkRejected.
export function classifyLink(url) {
  let raw = typeof url === 'string' ? url.trim() : '';
  if (!raw) throw new LinkRejected('Paste a link that starts with http:// or https://');
  if (raw.length > MAX_URL_LENGTH) throw new LinkRejected(`That link is too long (${MAX_URL_LENGTH} characters max)`);
  if (BAD_CHARS_RE.test(raw)) throw new LinkRejected("Links can't contain spaces, line breaks or backslashes");
  if (raw.startsWith('//')) raw = `https:${raw}`;
  else if (!SCHEME_RE.test(raw)) raw = `https://${raw}`;
  let u;
  try {
    u = new URL(raw);
  } catch {
    throw new LinkRejected("That doesn't look like a valid link");
  }
  const scheme = u.protocol.replace(/:$/, '').toLowerCase();
  if ((scheme !== 'http' && scheme !== 'https') || !u.hostname) {
    throw new LinkRejected('Links must start with http:// or https://');
  }
  // WHATWG URL repairs "https:example.com/a.pdf", "http:/x.com" and "https:///x.com"
  // into a host; the backend's urlsplit finds no host there and rejects them, so
  // the web rejects them too (Fix 6D D6).
  const afterScheme = raw.slice(raw.indexOf(':') + 1);
  if (!afterScheme.startsWith('//') || afterScheme.startsWith('///')) {
    throw new LinkRejected('Links must start with http:// or https://');
  }
  if (raw.slice(0, scheme.length) !== scheme) raw = scheme + raw.slice(scheme.length);
  const host = u.hostname.toLowerCase();
  const path = u.pathname || '';
  let kind = null;
  for (const [hosts, k] of HOST_KINDS) {
    if (hosts.some(h => hostMatches(host, h))) { kind = k; break; }
  }
  if (!kind) {
    if (path.toLowerCase().endsWith('.pdf')) kind = 'pdf';
    else if (AUDIO_RE.test(path)) kind = 'audio';
    else kind = 'other';
  }
  return {
    url: raw,
    kind,
    video_id: kind === 'youtube' ? youtubeVideoId(u) : null,
    label: DEFAULT_LABELS[kind],
    own_storage_warning: host.endsWith('supabase.co') && path.includes('/post-images/'),
  };
}

export const kindAllowsKey = (kind) => KEY_KINDS.includes(kind);
